import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XIcon } from '@heroicons/react/outline';
import PeoplePanel from './PeoplePanel';

/**
 * PeopleDialog
 * ------------
 * Purpose:
 *   - Present the "People involved in this referral" context as an on‑demand modal.
 *   - Keeps main pages focused on task/content while still offering quick access
 *     to referral participants (referrer, guardians, teachers, clinicians, etc.).
 *
 * Usage:
 *   <PeopleDialog
 *     isOpen={isOpen}
 *     onOpenChange={setIsOpen}
 *     patientRecordId={patientRecordId}
 *   />
 *
 * Design decisions:
 *   - Uses Radix Dialog for accessibility: focus trapping, ESC to close, ARIA labelling.
 *   - Blurred, semi‑transparent overlay to reinforce modality without feeling heavy.
 *   - Content scrolls within a rounded container to avoid full‑screen takeovers on mobile.
 */
const PeopleDialog = ({ isOpen, onOpenChange, patientRecordId, viewerRole }) => {
  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="radix-dialog-content-centered-motion fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-[480px] max-h-[85vh] bg-white rounded-xl shadow-2xl p-4 md:p-6 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <Dialog.Title className="text-lg md:text-xl font-bold text-gray-900">
              People Involved in this Referral
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            Details of participants related to this referral.
          </Dialog.Description>
          <div className="mt-2">
            <PeoplePanel patientRecordId={patientRecordId} viewerRole={viewerRole} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PeopleDialog;


